import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { Form } from 'react-final-form';

import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { Dialog } from 'components/common';
import { Input } from 'components/form';
import { formatCentsPrice } from 'services/helpers/formatPrice';

import {
  AchAllocation,
  AchTransactionRecord,
  BankVerificationState,
  ReleaseAchFundsResponse,
  ReleaseAchFundsFormValues,
  ReleaseAchFundsFormErrors,
  VERDICT_PENDING,
  VERDICT_POSTED,
} from './achVerificationTypes';
import {
  apiErrorMessage,
  formatDate,
  formatDateTime,
  parseUsdToCents,
  shortIntent,
  verdictLabel,
} from './achVerificationHelpers';

const useStyles = makeStyles((theme) => ({
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: theme.spacing(1),
  },

  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    fontSize: 13,
  },

  label: {
    color: theme.palette.text.secondary,
  },

  evidence: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontSize: 13,
  },

  chipPosted: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    height: 20,
  },

  chipPending: {
    backgroundColor: '#fff4e5',
    color: '#b26a00',
    height: 20,
  },

  chipMuted: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
    height: 20,
  },

  strong: {
    fontWeight: 600,
  },

  warning: {
    marginTop: theme.spacing(1.5),
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  // called after a successful release (the transactions list itself is reloaded through react-admin's refresh)
  onReleased?: (response: ReleaseAchFundsResponse) => void;
  // rollo user id (uuid)
  userId: string;
  // transaction group record of the pending ACH deposit
  record: AchTransactionRecord;
}

/**
 * RLPR-20420 admin early release of the pending part of an ACH deposit. Stripe still settles the debit; the
 * release only credits the amount now and moves it from the pending to the instant part of the allocation. The
 * latest bank transactions check stored on the deposit is shown as evidence and referenced in the audit log.
 */
const ReleaseAchFundsDialog = ({ open, onClose, onReleased, userId, record }: Props) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [error, setError] = useState<string | undefined>(undefined);
  const mountedRef = useRef(false);
  const pendingRef = useRef(false);
  const requestSequenceRef = useRef(0);

  const paymentIntent: string | undefined = record?.details?.paymentIntentId;
  const allocation: AchAllocation | undefined = record?.details?.achAllocation;
  const verification: BankVerificationState | undefined = record?.details?.bankVerification;
  const pendingCents = allocation?.pendingAmountCents ?? 0;
  const checked = !!verification?.lastCheckedAt;
  const posted = verification?.lastVerdict === VERDICT_POSTED;

  useLayoutEffect(() => {
    mountedRef.current = true;
    pendingRef.current = false;
    setError(undefined);
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, [userId, paymentIntent]);

  const validate = (values: ReleaseAchFundsFormValues): ReleaseAchFundsFormErrors => {
    const errors: ReleaseAchFundsFormErrors = {};
    const cents = parseUsdToCents(values.amount);
    if (cents === null) {
      errors.amount = 'Enter a positive USD amount with at most two decimal places';
    } else if (cents > pendingCents) {
      errors.amount = `At most ${formatCentsPrice(pendingCents)} is pending`;
    }
    if (!values.reason || !values.reason.trim()) {
      errors.reason = 'A reason is required, it goes to the audit log';
    }
    return errors;
  };

  const release = async (values: ReleaseAchFundsFormValues) => {
    if (pendingRef.current || !mountedRef.current) {
      return;
    }
    const errors = validate(values);
    if (Object.keys(errors).length) {
      return errors;
    }
    const amountCents = parseUsdToCents(values.amount);
    pendingRef.current = true;
    const requestId = ++requestSequenceRef.current;
    const isCurrentRequest = () => mountedRef.current && requestSequenceRef.current === requestId;
    try {
      setError(undefined);
      const { data } = await dataProvider.post('releaseAchFunds', {
        userId,
        data: {
          paymentIntent,
          amountCents,
          reason: values.reason.trim(),
          bankTransactionId:
            posted || verification?.lastVerdict === VERDICT_PENDING
              ? verification?.lastTransactionId
              : undefined,
        },
      });
      notify(
        `Released ${formatCentsPrice(data.releasedAmountCents)} to the user's balance`,
        'info'
      );
      if (isCurrentRequest() && onReleased) {
        onReleased(data);
      }
      if (isCurrentRequest()) {
        onClose();
      }
      // Synchronize the saved release even after navigation removed its dialog.
      refresh();
    } catch (e) {
      if (isCurrentRequest()) {
        setError(apiErrorMessage(e, 'Could not release the funds'));
      }
    } finally {
      if (isCurrentRequest()) {
        pendingRef.current = false;
      }
    }
  };

  const handleClose = () => {
    if (!pendingRef.current) {
      onClose();
    }
  };

  const overrideCents = verification?.lastAmountOverrideCents;
  const defaultReason = posted
    ? `Bank debit ${formatCentsPrice(
        Math.abs(verification?.lastTransactionAmountCents ?? 0)
      )} posted on ${formatDate(verification?.lastTransactionPostedAt)} (${
        verification?.lastTransactionId
      })${overrideCents ? ' matched with a test-mode amount override' : ''}`
    : '';

  const chipClass = posted
    ? classes.chipPosted
    : verification?.lastVerdict === VERDICT_PENDING
      ? classes.chipPending
      : classes.chipMuted;

  return (
    <Form<ReleaseAchFundsFormValues>
      onSubmit={release}
      validate={validate}
      initialValues={{ amount: (pendingCents / 100).toFixed(2), reason: defaultReason }}
      render={({ submitting, handleSubmit }) => (
        <Dialog
          open={open}
          title="Release pending ACH funds"
          onCancel={handleClose}
          onConfirm={handleSubmit}
          confirmText="Release funds"
          confirmDisabled={!paymentIntent || pendingCents <= 0}
          submitting={submitting}
          dismissDisabled={submitting}
          error={error}
          note="Stripe still settles the debit on its own schedule. If it fails or is cancelled later, the released amount is reversed like any instant credit, and it counts against the user's instant availability cap until settlement.">
          <Box className={classes.rows}>
            <Box className={classes.row}>
              <span className={classes.label}>Deposit</span>
              <span>{shortIntent(paymentIntent)}</span>
            </Box>
            <Box className={classes.row}>
              <span className={classes.label}>Deposit amount</span>
              <span>{formatCentsPrice(allocation?.baseAmountCents ?? 0)}</span>
            </Box>
            <Box className={classes.row}>
              <span className={classes.label}>Already available</span>
              <span>{formatCentsPrice(allocation?.instantAmountCents ?? 0)}</span>
            </Box>
            <Box className={classes.row}>
              <span className={classes.label}>Still pending</span>
              <span className={classes.strong}>{formatCentsPrice(pendingCents)}</span>
            </Box>
          </Box>

          <Box className={classes.evidence}>
            <Chip
              size="small"
              label={checked ? verification?.lastVerdict : 'NOT CHECKED'}
              className={chipClass}
            />
            <span>
              {checked ? (
                <>
                  {verdictLabel(verification?.lastVerdict)}, checked{' '}
                  {formatDateTime(verification?.lastCheckedAt)} by{' '}
                  {verification?.lastCheckedBy || 'an admin'}
                  {verification?.lastTransactionId
                    ? `. Bank transaction ${verification.lastTransactionId}${
                        verification.lastTransactionPostedAt
                          ? ` posted ${formatDate(verification.lastTransactionPostedAt)}`
                          : ''
                      }.`
                    : '.'}
                  {overrideCents
                    ? ` Matched against a Stripe test-mode override of ${formatCentsPrice(
                        overrideCents
                      )}, not the deposit amount.`
                    : ''}
                </>
              ) : (
                'The bank transactions were not checked for this deposit. Prefer "Check bank transactions" first: a posted debit is the evidence that the money left the user\'s account.'
              )}
            </span>
          </Box>

          <Input name="amount" label="Amount to release (USD)" disabled={submitting} />
          <Input name="reason" label="Reason (audit log)" disabled={submitting} />

          <Typography className={classes.warning}>
            The amount is credited to the user&apos;s balance immediately inside the deposit&apos;s
            transaction group.
          </Typography>
        </Dialog>
      )}
    />
  );
};

export default ReleaseAchFundsDialog;
